import { ApiConfig, handleResponse } from './config';
import { Module, ModuleContent } from '../../types/module';
import { ValidationError } from './errors';

export interface ModuleCreate extends Omit<Module, 'id' | 'createdAt' | 'updatedAt'> {}
export interface ModuleUpdate extends Partial<ModuleCreate> {}

export class ModuleService {
  constructor(private config: ApiConfig) {}

  // Get current auth headers with fresh token
  private getAuthHeaders(): Record<string, string> {
    const headers = { ...this.config.headers };
    
    // Try to get fresh token from auth store
    if (typeof window !== 'undefined') {
      try {
        const authStorage = JSON.parse(localStorage.getItem('auth-storage') || '{}');
        const token = authStorage.state?.token;
        if (token && token !== 'null' && token !== 'undefined' && token !== '') {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Keep existing headers if we can't get fresh token
      }
    }
    
    return headers;
  }

  async getModules(): Promise<Module[]> {
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules`, {
      headers
    });

    return handleResponse(response);
  }

  async getModuleById(id: string): Promise<Module> {
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules/${id}`, {
      headers
    });

    return handleResponse(response);
  }

  async createModule(module: ModuleCreate): Promise<Module> {
    if (!module.title || !module.description) {
      throw new ValidationError('Module title and description are required');
    }

    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules`, {
      method: 'POST',
      headers,
      body: JSON.stringify(module)
    });

    return handleResponse(response);
  }

  async updateModule(id: string, module: ModuleUpdate): Promise<Module> {
    console.log('API updateModule called with:', {
      id,
      moduleKeys: Object.keys(module),
      contentCount: module.content?.length || 0,
      contentSample: module.content?.[0] ? {
        title: module.content[0].title,
        contentLength: module.content[0].content?.length || 0,
        imageCount: (module.content[0].content?.match(/<img[^>]*>/g) || []).length
      } : null
    });

    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(module)
    });

    const result = await handleResponse(response);
    
    console.log('API updateModule response:', {
      success: !!result,
      resultContentCount: result?.content?.length || 0,
      resultContentSample: result?.content?.[0] ? {
        title: result.content[0].title,
        contentLength: result.content[0].content?.length || 0,
        imageCount: (result.content[0].content?.match(/<img[^>]*>/g) || []).length
      } : null
    });

    return result;
  }

  async deleteModule(id: string): Promise<void> {
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules/${id}`, {
      method: 'DELETE',
      headers
    });

    await handleResponse(response);
  }

  async addContent(moduleId: string, content: ModuleContent): Promise<Module> {
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules/${moduleId}/content`, {
      method: 'POST',
      headers,
      body: JSON.stringify(content)
    });

    return handleResponse(response);
  }

  async updateContent(moduleId: string, contentId: string, content: Partial<ModuleContent>): Promise<Module> {
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules/${moduleId}/content/${contentId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(content)
    });

    return handleResponse(response);
  }

  async deleteContent(moduleId: string, contentId: string): Promise<Module> {
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.config.baseUrl}/modules/${moduleId}/content/${contentId}`, {
      method: 'DELETE',
      headers
    });

    return handleResponse(response);
  }
}
